<template>
  <pop-up
    title="Alert Form"
    button-text="Submit"
    :disabled="loadingAlert || updatingAlert || addingAlert"
    :spinning="loadingAlert || updatingAlert || addingAlert"
    class="top-fade-in"
    @add="validateForm"
    @close="$emit('close')"
  >
    <transition
      name="fade"
      mode="out-in"
    >
      <div class="alert-table-wrapper">
        <loading-page v-if="loadingAlert || updatingAlert || addingAlert" />
        <validation-observer
          v-else
          ref="alertForm"
          tag="div"
        >
          <div class="row">
            <validation-provider
              #default="{ errors }"
              name="Name"
              class="col-12"
              rules="required|min:3|max:100"
            >
              <input-field
                label="Name*"
                label-position="top"
                :error="errors.length ? errors[0] : null"
              >
                <b-form-input
                  v-model.trim="alert.name"
                  :state="errors.length > 0 ? false : null"
                />
              </input-field>
            </validation-provider>
          </div>
          <div class="row">
            <validation-provider
              #default="{ errors }"
              name="Healthcheck Group"
              class="col-12 col-md-6 pr-md-1"
              rules="required"
            >
              <input-field
                label="Healthcheck Group*"
                label-position="top"
                :error="errors.length ? errors[0] : null"
              >
                <group-options
                  v-model="healthcheckGroupId"
                  :selected-group-id="healthcheckGroupId"
                  class="w-100"
                />
              </input-field>
            </validation-provider>
            <validation-provider
              #default="{ errors }"
              name="Healthcheck"
              class="col-12 col-md-6"
              rules="required"
            >
              <input-field
                label="Healthcheck*"
                label-position="top"
                :error="errors.length ? errors[0] : null"
              >
                <v-select
                  v-model="alert.healthcheck"
                  class="w-100 healthcheck-select"
                  label="name"
                  :options="healthchecks"
                  :filterable="false"
                  :loading="fetchingHealthchecks"
                  @open="onOpen(true)"
                  @close="onClose(true)"
                  @search="onHealthcheckSearch"
                >
                  <template #list-footer>
                    <li
                      v-if="hasHealthcheckNextPage && !healthcheckSearch"
                      ref="healthcheckLoad"
                      class="text-center"
                    >
                      <b-spinner
                        variant="primary"
                        small
                      />
                    </li>
                  </template>
                </v-select>
              </input-field>
            </validation-provider>
            <validation-provider
              #default="{ errors }"
              name="Providers"
              class="col-12"
              rules="required"
            >
              <input-field
                label="Providers*"
                label-position="top"
                class="providers-select"
                :error="errors.length ? errors[0] : null"
              >
                <v-select
                  v-model="selected"
                  label="name"
                  multiple
                  :options="providers"
                  :filterable="false"
                  place
                  @open="onOpen"
                  @close="onClose"
                  @search="onProviderSearch"
                >
                  <template #list-footer>
                    <li
                      v-if="hasProviderNextPage && !providerSearch"
                      ref="providerLoad"
                      class="text-center"
                    >
                      <b-spinner
                        variant="primary"
                        small
                      />
                    </li>
                  </template>
                </v-select>
              </input-field>
            </validation-provider>
            <validation-provider
              #default="{ errors }"
              name="Message"
              class="col-12"
              rules="required|max:4000"
            >
              <input-field
                label="Message*"
                label-position="top"
                class="alert-message"
                :error="errors.length ? errors[0] : null"
              >
                <template>
                  <at-ta
                    :members="messageHelpers"
                    class="w-100"
                  >
                    <b-form-textarea
                      v-model="alert.message"
                      class="message-field"
                      max-rows="5"
                      no-resize
                    />
                  </at-ta>
                </template>
              </input-field>
            </validation-provider>
            <div class="col-12">
              <custom-alert
                class="mr-35"
                items-center
                icon-name="InfoAlt"
              >
                Helpers with @ will be replaced accordingly to dynamic
                healthcheck data.
              </custom-alert>
            </div>
          </div>
        </validation-observer>
      </div>
    </transition>
  </pop-up>
</template>

<script>
import PopUp from '@/components/popup/Pop-up.vue'
import GroupOptions from '../components/GroupOptions.vue'

import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'

import AtTa from 'vue-at/dist/vue-at-textarea'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

import redirectError from '@/services/redirectError'

export default {
  name: 'AlertForm',
  components: {
    PopUp,
    GroupOptions,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    AtTa,
  },
  props: {
    details: {
      type: Boolean,
      default: false,
    },
    alertId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      required,
      selected: [],
      messageHelpers: ['alertName', 'projectId', 'healthcheckGroupName', 'healthcheckName', 'retries', 'status', 'timestamp', 'url', 'dataCenterName', 'reason'],
      fetchedHealthchecks: [],
      healthcheckSearch: '',
      healthcheckOffset: 0,
      healthcheckLimit: 10,
      healthcheckObserver: null,
      fetchedProviders: [],
      providerSearch: '',
      providerOffset: 0,
      providerLimit: 10,
      providerObserver: null,
      healthcheckGroupId: null,
    }
  },
  computed: {
    loadingAlert() {
      return this.$store.state.alerts.gettingAlert
    },
    fetchingHealthchecks() {
      return this.$store.state.healthchecks.fetchingHealthchecks
    },
    fetchingProviders() {
      return this.$store.state.providers.fetchingProviders
    },
    updatingAlert() {
      return this.$store.state.alerts.updatingAlert
    },
    addingAlert() {
      return this.$store.state.alerts.addingAlert
    },
    alert() {
      return this.$store.state.alerts.alert
    },
    healthchecks() {
      if (this.healthcheckSearch === '') {
        return this.fetchedHealthchecks
      }
      return this.fetchedHealthchecks.filter(healthcheck => healthcheck.name
        .toLowerCase()
        .includes(this.healthcheckSearch.toLowerCase()))
    },
    providers() {
      if (this.providerSearch === '') {
        return this.fetchedProviders
      }
      return this.fetchedProviders.filter(provider => provider.name.toLowerCase().includes(this.providerSearch.toLowerCase()))
    },
    hasHealthcheckNextPage() {
      return this.$store.state.healthchecks.healthchecksResultInfo.hasNextPage
    },
    hasProviderNextPage() {
      return this.$store.state.providers.providersResultInfo.hasNextPage
    },
  },
  watch: {
    healthcheckOffset() {
      this.getHealthchecks()
    },
    providerOffset() {
      this.getProviders()
    },
    healthcheckGroupId() {
      this.fetchedHealthchecks = []
      this.getHealthchecks()
    },
  },
  created() {
    this.getAlert()
    this.getProviders()
  },
  mounted() {
    this.healthcheckObserver = new IntersectionObserver(this.infiniteHScroll)
    this.providerObserver = new IntersectionObserver(this.infinitePScroll)
  },
  methods: {
    validateForm() {
      return new Promise((resolve, reject) => {
        this.$refs.alertForm.validate().then(success => {
          if (success) {
            this.prepareForm()
            resolve(true)
          } else {
            this.errorToast('Check if all fields are valid!')
            reject()
          }
        })
      })
    },
    onHealthcheckSearch(query) {
      this.healthcheckSearch = query
      // this.healthcheckOffset = 0
    },
    onProviderSearch(query) {
      this.providerSearch = query
      // this.providerOffset = 0
    },
    getAlert() {
      this.$store
        .dispatch('alerts/getAlert', this.details ? this.alertId : 0)
        .then(() => {
          if (this.alert.providers && this.alert.providers.length > 0) {
            this.alert.providers.forEach(provider => {
              this.selected.push(provider)
            })
          }
          if (!this.details || !this.alert.message) {
            this.alert.message = 'Snitch Alert for site: @healthcheckGroupName/@healthcheckName\nReason: @reason\nStatus: @status\nRetries: @retries\nData Center: @dataCenterName\nDateTime UTC: @timestamp'
          }
          if (this.details) {
            this.healthcheckGroupId = this.alert?.healthcheck?.groupId
          }
        })
        .catch(err => {
          redirectError(err)
        })
    },
    getHealthchecks() {
      this.$store
        .dispatch('healthchecks/fetchHealthchecks', {
          pageIndex: this.healthcheckOffset,
          pageSize: this.healthcheckLimit,
          groupId: this.healthcheckGroupId || 0,
        })
        .then(res => {
          this.fetchedHealthchecks.push(...res.data.result)
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching healthchecks!',
          )
        })
    },
    getProviders() {
      this.$store
        .dispatch('providers/fetchProviders', {
          pageIndex: this.providerOffset,
          pageSize: this.providerLimit,
        })
        .then(res => {
          this.fetchedProviders.push(...res.data.result)
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching providers!',
          )
        })
    },
    onCancel() {
      if (this.details) {
        return this.$router.push({
          name: 'alerts-details',
          params: { alertId: this.alertId },
        })
      }
      return this.$router.push({ name: 'alerts' })
    },
    prepareForm() {
      this.alert.healthcheckId = this.alert.healthcheck.id
      this.alert.providers = []
      if (this.selected.length > 0) {
        this.selected.forEach(provider => {
          this.alert.providers.push(provider.id)
        })
      }
      this.submit()
    },
    submit() {
      this.$emit('submit', this.alert)
    },
    async onOpen(hch = false) {
      if (hch ? this.hasHealthcheckNextPage : this.hasProviderNextPage) {
        await this.$nextTick()
        if (hch) {
          this.healthcheckObserver.observe(this.$refs.healthcheckLoad)
        } else {
          this.providerObserver.observe(this.$refs.providerLoad)
        }
      }
    },
    onClose(hch = false) {
      if (hch) {
        this.healthcheckObserver.disconnect()
      } else {
        this.providerObserver.disconnect()
      }
    },
    async infiniteHScroll([{ isIntersecting, target }]) {
      if (isIntersecting) {
        const ul = target.offsetParent
        const { scrollTop } = target.offsetParent
        this.healthcheckOffset++
        await this.$nextTick()
        ul.scrollTop = scrollTop
      }
    },
    async infinitePScroll([{ isIntersecting, target }]) {
      if (isIntersecting) {
        const ul = target.offsetParent
        const { scrollTop } = target.offsetParent
        this.providerOffset++
        await this.$nextTick()
        ul.scrollTop = scrollTop
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.alert-table-wrapper {
  .pagination {
    display: flex;
    margin: 0.25rem 0.25rem 0;

    .pagination-buttons {
      flex-grow: 1;
      &:hover {
        cursor: pointer;
      }
    }
  }
  .healthcheck-select {
    ::v-deep {
      .vs__dropdown-toggle {
        min-height: 42px;
        max-height: 42px;
        overflow: hidden;
        padding-bottom: 0;
        font-size: 1rem;
        .vs__selected {
          color: #052d61;
          margin-top: 0;
          height: 100%;
        }
      }
      .vs__dropdown-menu {
        font-size: 1rem;
        max-height: 300px;
      }
    }
  }
  .providers-select {
    ::v-deep {
      .v-select {
        width: 100%;
        .vs__dropdown-toggle {
          min-height: 42px;
          .vs__selected {
            max-height: 25px;
          }

          .vs__actions {
            padding-right: 0;
            svg {
              margin: auto 13.5px;
            }
          }

          &::after {
            content: '';
            width: 1px;
            height: 100%;
            position: absolute;
            right: 42px;
            top: 0;
            background-color: #e0e5eb;
            transition: all 0.3s;
            @at-root .vs--open#{&} {
              background-color: var(--primary);
            }
          }
        }
        .vs__dropdown-menu {
          font-size: 1rem;
          max-height: 300px;
        }
      }
    }
  }
  .message-field {
    height: 100%;
  }
}
</style>
